/**
 * Return-accessories hook — P0-2 FE-3 D3.
 * A bérlés-visszavételi tartozék-checklist READ-forrása (`GET /rentals/:id/return-accessories`).
 * `useConsignmentsForRental` mintára (useState + useEffect, NEM React Query).
 *
 * A `status` nullable: a render-start `null` (még-nem-ellenőrzött); az operátor a checklisten
 * tölti, a kitöltött eredmény a finalize-kor a return-doc body-jába megy (ephemeral, persist=Phase-2).
 */
import { getReturnAccessories } from '@/api/return-doc';
import type { RentalAccessoryCheckItem } from '@kgc/rental-core/rental-dto';
import { useCallback, useEffect, useState } from 'react';

interface UseReturnAccessoriesResult {
  accessories: RentalAccessoryCheckItem[];
  isLoading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

export function useReturnAccessories(rentalId: string | undefined): UseReturnAccessoriesResult {
  const [accessories, setAccessories] = useState<RentalAccessoryCheckItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchAccessories = useCallback(async () => {
    if (!rentalId) {
      setAccessories([]);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);
    try {
      const response = await getReturnAccessories(rentalId);
      setAccessories(response.data);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to fetch return accessories';
      setError(message);
      setAccessories([]);
    } finally {
      setIsLoading(false);
    }
  }, [rentalId]);

  useEffect(() => {
    void fetchAccessories();
  }, [fetchAccessories]);

  return { accessories, isLoading, error, refetch: fetchAccessories };
}
